import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { homeTranslations, type TranslationKey } from '@/i18n/homeTranslations'

export type Lang = 'it' | 'en'

type LanguageContextValue = {
  language: Lang
  setLanguage: (language: Lang) => void
  toggleLanguage: () => void
  t: (key: TranslationKey) => string
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined)

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, updateLanguage] = useState<Lang>(() => {
    if (typeof window === 'undefined') return 'en'
    return window.localStorage.getItem('language') === 'it' ? 'it' : 'en'
  })

  const setLanguage = useCallback((nextLanguage: Lang) => {
    updateLanguage(nextLanguage)
    window.localStorage.setItem('language', nextLanguage)
    document.documentElement.lang = nextLanguage
  }, [])

  const toggleLanguage = useCallback(() => {
    updateLanguage(current => {
      const nextLanguage = current === 'it' ? 'en' : 'it'
      window.localStorage.setItem('language', nextLanguage)
      document.documentElement.lang = nextLanguage
      return nextLanguage
    })
  }, [])

  useEffect(() => {
    document.documentElement.lang = language
    window.localStorage.setItem('language', language)
  }, [language])

  const t = useCallback((key: TranslationKey) => homeTranslations[language][key], [language])
  const value = useMemo(() => ({ language, setLanguage, toggleLanguage, t }), [language, setLanguage, toggleLanguage, t])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) throw new Error('useLanguage must be used within LanguageProvider')
  return context
}
